// The onboarding path — design `3b`.
//
// There is exactly ONE path (D18): the seeded `core-compliance` checklist,
// walked in submission order, framed as "get the report filed". The earlier
// design offered a second framing ("understand my costs") over the same seven
// keys; it was dropped, so what remains is an ordering plus the copy that goes
// with it, not a choice.
//
// Everything here is client-side (D1 in docs/features/onboarding-guided-path).

import { getItemMeta } from './checklistItemMeta';
import { identityT } from 'helpers/i18nFallback';

// The year a declarant is collecting FOR — this one. Its declaration is filed
// the year after, which is why the headline says "your 2026 report" while the
// deadline sits in 2027. Derived rather than written down twice, so this
// doesn't quietly go stale on 1 January.
const REPORTING_YEAR = new Date().getFullYear();

// UK CBAM's first accounting period is calendar 2027 (FA 2026 Sch 17) — there
// is no earlier report to file, so the UK headline never names a year before
// it, even while the declarant onboards in 2026.
const UK_FIRST_REPORTING_YEAR = 2027;

/**
 * Submission order: who you buy from → where it's made → what you brought in →
 * its emissions → the factor → the file. Structural, so it needs no `t`.
 */
export const PATH_ORDER = [
  'upload-suppliers',
  'upload-installations',
  'upload-imports',
  'upload-emissions',
  'calculate-sefa',
  'generate-report',
  'connect-supplier',
];

/**
 * A builder: every rendered string resolves through `t`, with an explicit
 * namespace because there is no enclosing useTranslation() here
 * (docs/features/declarant-i18n/CONVENTIONS.md). `order` is structural and
 * stays literal.
 */
export const buildOnboardingPath = (t = identityT, { ukVariant = false } = {}) => ({
  // The panel's headline — what the whole rail is FOR, said once at the top.
  panelTitle: t('onboarding:paths.report.panelTitle', 'Your path to a filed {{year}} report', {
    year: ukVariant ? Math.max(REPORTING_YEAR, UK_FIRST_REPORTING_YEAR) : REPORTING_YEAR,
  }),
  order: PATH_ORDER,
});

// What each upload step accepts (D5). There are only ever two files in play —
// our workbook and the official EU one — and both land in the same Upload hub,
// so the options are a *reading* of the step, not a set of destinations: one
// Upload button and one "How it works" serve every option under a step.
//
// Titles and icons are shared so the same file reads the same wherever it
// appears; the blurb is per-step, because what each file is FOR changes.
// The rendered title is resolved by OnboardingUploadOptions from `id`, so no
// title lives here — only the identity the renderer switches on. The blurb IS
// rendered, so it arrives already resolved through `t` from `buildStepUploads`.
const cgSheet = (blurb, badge) => ({
  id: 'cg-sheet',
  icon: 'excel',
  badge,
  blurb,
});
const euComms = (blurb) => ({
  id: 'eu-comms',
  icon: 'eu',
  blurb,
});
const hmrcExport = (blurb, badge) => ({
  id: 'hmrc-export',
  icon: 'hmrc',
  badge,
  blurb,
});

// Said on every data step, because the path's one-step-at-a-time shape hides
// it: the workbook has a sheet per step and the hub walks whichever ones your
// file carries (src/modules/UploadHub/docs/README.md — "CG Templates (Up to 6
// Sheets)"), so all four steps can be one upload. The path stays step-by-step
// for anyone whose data arrives that way; this is the shortcut for anyone whose
// doesn't.
export const uploadHubNote = (t = identityT) =>
  t(
    'onboarding:uploads.hubNote',
    'Already have the rest? One filled workbook does suppliers, installations, imports and emissions in a single pass through the Upload hub.',
  );

// Which steps the CBAM Communication Template can actually stand in for is not a
// copy decision — it's what the hub does with the file. Its resolution flow
// matches or CREATES a supplier, then matches or CREATES that supplier's
// installation, then posts the emissions it carries
// (src/modules/UploadHub/docs/README.md — "EFDA/COMMS Templates (4 Steps)"), so
// it genuinely covers three of the four steps. It carries no shipment rows:
// imports land only through the CG workbook's Imports sheet
// (`POST /shipments/import`), so listing it there would send a declarant to the
// hub with a file that cannot finish the step.
//
// A builder, like the path above: hints and blurbs are rendered copy, so they
// resolve through `t` with explicit namespaces (no enclosing useTranslation()
// here). `id`, `icon` and the step keys are structural and stay literal.
//
// UK (`ukVariant`): the Communication Template is an EU regulatory artifact, so
// it never appears — entity and emissions steps are workbook-only. What UK
// declarants DO have ready-made is the HMRC Customs Report (the same document
// the /uk/imports source picker recommends: every declaration they've made,
// free from the HMRC portal), and it carries exactly the customs rows the EU
// template lacks — so it appears on the one step the EU file couldn't cover.
export const buildStepUploads = (t = identityT, { ukVariant = false } = {}) => {
  if (ukVariant) {
    return {
      'upload-suppliers': {
        options: [
          cgSheet(
            t(
              'onboarding:uploads.suppliers.cgSheet',
              'Our workbook — its Suppliers tab is the whole list in one go',
            ),
          ),
        ],
      },
      'upload-installations': {
        options: [
          cgSheet(
            t(
              'onboarding:uploads.installations.cgSheet',
              'The same workbook — fill the Installations tab',
            ),
          ),
        ],
      },
      'upload-imports': {
        hint: t(
          'onboarding:uploads.hint.ukImports',
          "Either file works — the HMRC report is a ready-made record of every declaration you've made.",
        ),
        options: [
          hmrcExport(
            t(
              'onboarding:uploads.imports.hmrcExport',
              'Your import declarations exported from HMRC — free to request, arrives within 72 hours',
            ),
            t('onboarding:uploads.hmrcExport.badge', 'Recommended'),
          ),
          cgSheet(
            t(
              'onboarding:uploads.imports.cgSheet',
              'The same workbook — CN code, quantity, origin, one row per shipment',
            ),
          ),
        ],
      },
      'upload-emissions': {
        options: [
          cgSheet(
            t(
              'onboarding:uploads.emissions.cgSheet',
              "Our workbook — best if they've never reported CBAM",
            ),
          ),
        ],
      },
    };
  }

  return {
    'upload-suppliers': {
      hint: t(
        'onboarding:uploads.hint.eitherFile',
        'Either file works — the workbook does them all at once, a template one at a time.',
      ),
      options: [
        cgSheet(
          t(
            'onboarding:uploads.suppliers.cgSheet',
            'Our workbook — its Suppliers tab is the whole list in one go',
          ),
        ),
        euComms(
          t(
            'onboarding:uploads.suppliers.euComms',
            "Creates the supplier from a filled template's operator details",
          ),
        ),
      ],
    },
    'upload-installations': {
      hint: t(
        'onboarding:uploads.hint.eitherFile',
        'Either file works — the workbook does them all at once, a template one at a time.',
      ),
      options: [
        cgSheet(
          t(
            'onboarding:uploads.installations.cgSheet',
            'The same workbook — fill the Installations tab',
          ),
        ),
        euComms(
          t(
            'onboarding:uploads.installations.euComms',
            'Creates the installation, and loads the emissions it carries',
          ),
        ),
      ],
    },
    'upload-imports': {
      // No EU option: the template carries emissions, not customs rows, and
      // shipments only come from the workbook's Imports sheet.
      options: [
        cgSheet(
          t(
            'onboarding:uploads.imports.cgSheet',
            'The same workbook — CN code, quantity, origin, one row per shipment',
          ),
        ),
      ],
    },
    'upload-emissions': {
      // The one step where the file has to come from someone else first — hence
      // the extra action.
      hint: t(
        'onboarding:uploads.hint.supplierFills',
        'Either file works — whichever your supplier can fill in.',
      ),
      options: [
        cgSheet(
          t(
            'onboarding:uploads.emissions.cgSheet',
            "Our workbook — best if they've never reported CBAM",
          ),
        ),
        euComms(
          t(
            'onboarding:uploads.emissions.euComms',
            'Official EFDA / COMMS file they may already fill in',
          ),
        ),
      ],
    },
  };
};

/**
 * Sort the API's items into path order. Total and stable: any key the path
 * doesn't know about is appended in API order, so a seed change can add a step
 * without the panel silently dropping it.
 *
 * @param {Array<{key:string}>} items
 */
export const sortItemsForPath = (items = []) => {
  const rank = (item) => {
    const index = PATH_ORDER.indexOf(item.key);
    return index === -1 ? PATH_ORDER.length : index;
  };
  return items
    .map((item, apiIndex) => ({ item, apiIndex, rank: rank(item) }))
    .sort((a, b) => a.rank - b.rank || a.apiIndex - b.apiIndex)
    .map((entry) => entry.item);
};

/**
 * Everything the panel needs to describe progress along the path.
 *
 * @param {Array} sortedItems  output of `sortItemsForPath`
 */
export const getPathStats = (sortedItems = []) => {
  const total = sortedItems.length;
  const completedCount = sortedItems.filter((item) => item.completed).length;
  const currentIndex = sortedItems.findIndex((item) => !item.completed);
  const minutesLeft = sortedItems
    .filter((item) => !item.completed)
    .reduce((sum, item) => sum + getItemMeta(item).minutes, 0);

  return {
    total,
    completedCount,
    currentIndex,
    percentage: total > 0 ? Math.round((completedCount / total) * 100) : 0,
    minutesLeft,
  };
};

/**
 * The nearest incomplete step *earlier in path order* than `index` — the reason
 * a later step reads as locked. Presentational only: nothing is actually gated.
 */
export const findBlockingStep = (sortedItems = [], index = 0) =>
  sortedItems.slice(0, index).find((item) => !item.completed) ?? null;
